import _ from 'lodash';
import React from 'react';
import dayjs from 'dayjs';

import { DateInput, Form, Grid, Tooltip } from '@mairistem/composants';
import { AdresseForm } from '@mairistem/adresses';

import { AssistantContext } from '@/contexts';
import { useTypesVoies } from '@/hooks';
import { Section, SectionHeader } from '@/components/Common';
import {
  fromAdresseFormReferentiel,
  paysTypeAdresse,
  toAssistantAdresseFormValue,
} from '@/utils';
import { COLORS } from '@/constants';

interface InformationsDeclarationProps {
  dateDeclaration?: string;
  attestationLienParente?: boolean;
  attestationAdresseCommune?: boolean;
  adresseDeclaration?: any;
  adresseCommune?: any;
  errors: Record<string, any>;
}

const InformationsDeclaration = (props: InformationsDeclarationProps) => {
  const {
    dateDeclaration,
    attestationLienParente,
    attestationAdresseCommune,
    adresseDeclaration,
    adresseCommune,
    errors,
  } = props;

  const { selectedDeclarationType, onChange } =
    React.useContext(AssistantContext);

  // Projet : les dates de signature sont facultatives (le projet peut être
  // créé sans), elles deviennent obligatoires à la déclaration initiale.
  const isProjet = selectedDeclarationType === 'projet';
  const typesVoies = useTypesVoies();

  const [dateDeclarationObj, setDateDeclarationObj] =
    React.useState<Date | null>(
      dateDeclaration ? new Date(dateDeclaration) : null,
    );

  React.useEffect(() => {
    setDateDeclarationObj(dateDeclaration ? new Date(dateDeclaration) : null);
  }, [dateDeclaration]);

  const formatConventionAdresse = React.useCallback(
    (baseAdresse: any, adresse: any) => ({
      ...baseAdresse,
      ...(!_.isNil(adresse?.type) ? { type: adresse.type } : {}),
      ...(!_.isNil(adresse?.codePostal)
        ? { codePostal: adresse.codePostal }
        : {}),
      ...(!_.isNil(adresse?.commune) ? { localite: adresse.commune } : {}),
      ...(!_.isNil(adresse?.voie) ? { libelleVoie: adresse.voie } : {}),
      ...(!_.isNil(adresse?.pays) ? { libellePays: adresse.pays } : {}),
      ...fromAdresseFormReferentiel(adresse),
      ...(!_.isNil(adresse?.numeroVoirie)
        ? { numeroVoie: adresse.numeroVoirie }
        : {}),
      ...(!_.isNil(adresse?.complementNumeroVoirie)
        ? { complementNumeroVoie: adresse.complementNumeroVoirie }
        : {}),
      ...(!_.isNil(adresse?.complementGeographique)
        ? { complementGeographique: adresse.complementGeographique }
        : {}),
      ...(!_.isNil(adresse?.complementDistribution)
        ? { complementDistribution: adresse.complementDistribution }
        : {}),
      ...(!_.isNil(adresse?.complementIdentification)
        ? { complementIdentification: adresse.complementIdentification }
        : {}),
    }),
    [],
  );

  const handleAdresseItemChange = React.useCallback(
    (field: string, baseAdresse: any) =>
      ({ adresse }) => {
        // AdresseForm émet parfois une adresse vide (mises à jour groupées) :
        // l'appliquer remettrait l'adresse d'origine par-dessus la saisie.
        if (_.isEmpty(adresse)) return;

        onChange(null, {
          name: `conventionDeclaration.${field}`,
          value: formatConventionAdresse(baseAdresse, { ...adresse }),
        });
      },
    [formatConventionAdresse, onChange],
  );

  const handleAdresseChange = React.useCallback(
    (field: string, baseAdresse: any) =>
      ({ target: { name, value } }) => {
        // Seul le type est écrit : remplacer l'adresse par `{ type }` effaçait
        // celle que `onItemChange` vient de réinitialiser (commune déléguée…).
        if (name === 'adresse.type') {
          onChange(null, {
            name: `conventionDeclaration.${field}.type`,
            value,
          });
          // La réinitialisation d'AdresseForm conserve le type de voie de
          // l'adresse précédente.
          onChange(null, {
            name: `conventionDeclaration.${field}.typeVoie`,
            value: '',
          });
          const pays = paysTypeAdresse(value);
          if (pays) {
            onChange(null, {
              name: `conventionDeclaration.${field}.libellePays`,
              value: pays,
            });
          }

          return;
        }

        const adresseField = _.replace(name, 'adresse.', '');

        onChange(null, {
          name: `conventionDeclaration.${field}`,
          value: formatConventionAdresse(baseAdresse, {
            [adresseField]: value,
          }),
        });
      },
    [formatConventionAdresse, onChange],
  );

  const handleAttestationChange = React.useCallback(
    (field: string, checked: boolean) => {
      onChange(null, {
        name: `conventionDeclaration.${field}`,
        value: checked,
      });
    },
    [onChange],
  );

  const handleAdresseFieldChange = React.useCallback(
    (
      adresseField: 'adresseCommune' | 'adresseDeclaration',
      field: string,
      value: string | number,
    ) => {
      onChange(null, {
        name: `conventionDeclaration.${adresseField}.${field}`,
        value,
      });
    },
    [onChange],
  );

  const handleDateDeclarationChange = React.useCallback(
    (value: string | Date | null) => {
      const rawValue = value;

      if (_.isNil(rawValue) || rawValue === '') {
        setDateDeclarationObj(null);
        onChange(null, {
          name: 'conventionDeclaration.dateDeclaration',
          value: null,
        });
        return;
      }

      const parsedDate =
        rawValue instanceof Date ? rawValue : new Date(rawValue);
      const nextDate = Number.isNaN(parsedDate.getTime()) ? null : parsedDate;

      setDateDeclarationObj(nextDate);
      onChange(null, {
        name: 'conventionDeclaration.dateDeclaration',
        value: nextDate ? dayjs(nextDate).format('YYYY-MM-DD') : null,
      });
    },
    [onChange],
  );

  return (
    <Section
      data-testid="assistant-declaration"
      title={`DÉCLARATION ${_.upperCase(selectedDeclarationType)}`}
    >
      {/* @ts-ignore */}
      <Form style={{ display: 'flex' }}>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            padding: 14,
          }}
        >
          <SectionHeader
            title="Attestations sur l'honneur"
            required={!isProjet}
          />
          <Grid padded>
            <Grid.Row>
              <Grid.Column width={16}>
                <Tooltip
                  trigger={
                    <Form.Checkbox
                      label="Qu'il n'existe aucun lien de parenté ou d'alliance qui constituerait un empêchement pour conclure un PACS"
                      data-testid="assistant-declaration-attestation-lien-parente"
                      checked={attestationLienParente}
                      onChange={(_e, data) =>
                        handleAttestationChange(
                          'attestationLienParente',
                          data?.checked,
                        )
                      }
                    />
                  }
                  type={errors?.attestationLienParente ? 'error' : ''}
                  message={errors?.attestationLienParente}
                />
              </Grid.Column>
            </Grid.Row>
            <Grid.Row>
              <Grid.Column width={16}>
                <Tooltip
                  trigger={
                    <Form.Checkbox
                      label="Établir la résidence commune à l'adresse suivante :"
                      data-testid="assistant-declaration-attestation-adresse-commune"
                      checked={attestationAdresseCommune}
                      onChange={(_e, data) =>
                        handleAttestationChange(
                          'attestationAdresseCommune',
                          data?.checked,
                        )
                      }
                    />
                  }
                  type={errors?.attestationResidenceCommune ? 'error' : ''}
                  message={errors?.attestationResidenceCommune}
                />
              </Grid.Column>
            </Grid.Row>
            <Grid.Row>
              <Grid.Column width={16}>
                <AdresseForm
                  error={!!errors?.adresseCommune}
                  disabled={!attestationAdresseCommune}
                  saisie="libre"
                  handleTypes
                  displayDepartement={false}
                  displayArticle
                  displayPays={false}
                  adresse={toAssistantAdresseFormValue(
                    adresseCommune,
                    typesVoies,
                  )}
                  onItemChange={handleAdresseItemChange(
                    'adresseCommune',
                    adresseCommune,
                  )}
                  onChange={handleAdresseChange(
                    'adresseCommune',
                    adresseCommune,
                  )}
                />
              </Grid.Column>
            </Grid.Row>
          </Grid>
          <Grid padded>
            <Grid.Row verticalAlign="middle" style={{ padding: 0 }}>
              <Grid.Column width={6}>
                <label>&nbsp;</label>
                <Form.Checkbox
                  disabled={!attestationAdresseCommune}
                  label="Commune déléguée"
                  data-testid="assistant-declaration-adresse-commune-commune-delegue"
                  checked={!!adresseCommune?.communeDelegue}
                  onChange={(_e, data) => {
                    handleAdresseFieldChange(
                      'adresseCommune',
                      'communeDelegue',
                      data?.checked ? 1 : 0,
                    );

                    if (!data?.checked) {
                      handleAdresseFieldChange(
                        'adresseCommune',
                        'libelleCommuneDelegue',
                        '',
                      );
                    }
                  }}
                />
              </Grid.Column>
              <Grid.Column width={10}>
                <Tooltip
                  trigger={
                    <div>
                      <Form.Input
                        label="&nbsp;"
                        input={{
                          'data-testid':
                            'assistant-declaration-adresse-commune-libelle-commune-delegue',
                        }}
                        value={adresseCommune?.libelleCommuneDelegue ?? ''}
                        disabled={
                          !attestationAdresseCommune ||
                          !adresseCommune?.communeDelegue
                        }
                        error={!!errors?.libelleCommuneDelegueCommune}
                        onChange={(_e, data) => {
                          handleAdresseFieldChange(
                            'adresseCommune',
                            'libelleCommuneDelegue',
                            data?.value ?? '',
                          );
                        }}
                      />
                    </div>
                  }
                  type={errors?.libelleCommuneDelegueCommune ? 'error' : ''}
                  message={errors?.libelleCommuneDelegueCommune}
                />
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </div>
        <div
          style={{
            border: `1px solid ${COLORS.DividerColor}`,
            margin: '0px 2rem',
            width: 1,
          }}
        />
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            padding: 14,
          }}
        >
          <SectionHeader title="Signature des partenaires" />
          <Grid padded>
            <Grid.Row style={{ paddingBottom: 0 }}>
              <Grid.Column
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  paddingTop: 0,
                  paddingBottom: 0,
                  width: 'auto',
                  minWidth: 'fit-content',
                }}
              >
                {/* @ts-ignore */}
                <Form.Field required={!isProjet} style={{ margin: 0 }}>
                  <label style={{ margin: 0, whiteSpace: 'nowrap' }}>
                    Signée le
                  </label>
                </Form.Field>
              </Grid.Column>
              <Grid.Column
                style={{
                  display: 'flex',
                  paddingTop: 0,
                  paddingBottom: 0,
                  width: '18%',
                }}
              >
                <Tooltip
                  trigger={
                    <div>
                      <DateInput
                        data-testid="assistant-declaration-date-signature"
                        value={dateDeclarationObj}
                        onChange={handleDateDeclarationChange}
                        error={errors?.dateDeclarationInitiale}
                      />
                    </div>
                  }
                  type={errors?.dateDeclarationInitiale ? 'error' : ''}
                  message={errors?.dateDeclarationInitiale}
                />
              </Grid.Column>
            </Grid.Row>
          </Grid>
          <SectionHeader
            title="Lieu de signature"
            style={{ marginBottom: 0 }}
          />
          <Grid padded>
            <Grid.Row>
              <Grid.Column width={16}>
                <AdresseForm
                  saisie="libre"
                  handleTypes
                  displayDepartement={false}
                  displayArticle
                  adresse={toAssistantAdresseFormValue(
                    adresseDeclaration,
                    typesVoies,
                  )}
                  onItemChange={handleAdresseItemChange(
                    'adresseDeclaration',
                    adresseDeclaration,
                  )}
                  onChange={handleAdresseChange(
                    'adresseDeclaration',
                    adresseDeclaration,
                  )}
                />
              </Grid.Column>
            </Grid.Row>
          </Grid>
          <Grid padded>
            <Grid.Row verticalAlign="middle" style={{ padding: 0 }}>
              <Grid.Column width={6}>
                <label>&nbsp;</label>
                <Form.Checkbox
                  label="Commune déléguée"
                  data-testid="assistant-declaration-signature-commune-delegue"
                  checked={!!adresseDeclaration?.communeDelegue}
                  onChange={(_e, data) => {
                    handleAdresseFieldChange(
                      'adresseDeclaration',
                      'communeDelegue',
                      data?.checked ? 1 : 0,
                    );

                    if (!data?.checked) {
                      handleAdresseFieldChange(
                        'adresseDeclaration',
                        'libelleCommuneDelegue',
                        '',
                      );
                    }
                  }}
                />
              </Grid.Column>
              <Grid.Column width={10}>
                <Tooltip
                  trigger={
                    <div>
                      <Form.Input
                        label="&nbsp;"
                        input={{
                          'data-testid':
                            'assistant-declaration-signature-libelle-commune-delegue',
                        }}
                        value={adresseDeclaration?.libelleCommuneDelegue ?? ''}
                        disabled={!adresseDeclaration?.communeDelegue}
                        error={!!errors?.libelleCommuneDelegueDeclaration}
                        onChange={(_e, data) => {
                          handleAdresseFieldChange(
                            'adresseDeclaration',
                            'libelleCommuneDelegue',
                            data?.value ?? '',
                          );
                        }}
                      />
                    </div>
                  }
                  type={errors?.libelleCommuneDelegueDeclaration ? 'error' : ''}
                  message={errors?.libelleCommuneDelegueDeclaration}
                />
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </div>
      </Form>
    </Section>
  );
};

export default InformationsDeclaration;
